import { Pencil, Pause, Play, Trash2 } from "lucide-react";
import { DataTablePattern } from "../patterns/DataTablePattern";
import { IconButton } from "../primitives/IconButton";
import { getPermissionSummary, getDefaultPermissions, type PermissionOverride } from "../../utils/permissions";
import { cn } from "../utils/cn";

type Membership = {
  id: string;
  organization_id: string;
  org_role: string;
  status: string;
  permissions_override?: PermissionOverride | null;
  organization?: {
    id: string;
    name: string;
    type: string;
    city?: string;
    province?: string;
  };
  user?: {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
    display_name?: string;
  };
};

type MembershipsTableProps = {
  memberships: Membership[];
  isSuperAdmin: boolean;
  onEdit: (membership: Membership) => void;
  onSuspend?: (membershipId: string) => void;
  onRemove?: (membershipId: string) => void;
};

const ORG_TYPE_LABELS: Record<string, string> = {
  xlkey_internal: "XLKey",
  farm: "Ferme",
  ambassador_org: "Ambassadeur",
  drone_pilot_org: "Pilote",
};

const ORG_TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  xlkey_internal: { bg: "bg-primary/10", text: "text-primary" },
  farm: { bg: "bg-success/10", text: "text-success" },
  ambassador_org: { bg: "bg-warning/10", text: "text-warning" },
  drone_pilot_org: { bg: "bg-info/10", text: "text-info" },
};

const ROLE_LABELS: Record<string, string> = {
  org_owner: "Propriétaire",
  org_admin: "Administrateur",
  manager: "Gestionnaire",
  operator: "Opérateur",
  sales_rep: "Représentant commercial",
  analyst: "Analyste",
  readonly: "Lecture seule",
  ambassador: "Ambassadeur",
};

export function MembershipsTable({
  memberships,
  isSuperAdmin,
  onEdit,
  onSuspend,
  onRemove,
}: MembershipsTableProps) {
  const getOrgTypeLabel = (type: string) => {
    return ORG_TYPE_LABELS[type] || type;
  };

  const getOrgTypeColor = (type: string) => {
    return ORG_TYPE_COLORS[type] || { bg: "bg-muted/10", text: "text-muted" };
  };

  const getRoleLabel = (role: string) => {
    return ROLE_LABELS[role] || role;
  };

  const getPermissionsSummary = (membership: Membership) => {
    if (isSuperAdmin) {
      return "Toutes les permissions";
    }
    const defaultPerms = getDefaultPermissions(membership.org_role);
    const override = membership.permissions_override;
    const allowCount = override?.allow?.length || 0;
    const denyCount = override?.deny?.length || 0;
    
    if (allowCount === 0 && denyCount === 0) {
      return `Héritées du rôle (${defaultPerms.length})`;
    }
    
    const parts: string[] = [];
    parts.push(`Héritées du rôle (${defaultPerms.length})`);
    if (allowCount > 0) {
      parts.push(`+${allowCount} ajoutée(s)`);
    }
    if (denyCount > 0) {
      parts.push(`-${denyCount} retirée(s)`);
    }
    return parts.join(", ");
  };

  const rows = memberships.map((membership) => {
    // For team view, show user info; for access view, show organization info
    const showUserInfo = membership.user && !membership.organization;
    const userName = membership.user
      ? membership.user.display_name ||
        `${membership.user.first_name || ""} ${membership.user.last_name || ""}`.trim() ||
        membership.user.email ||
        "—"
      : null;
    const userEmail = membership.user?.email || null;

    const orgType = membership.organization?.type || "";
    const orgTypeColor = getOrgTypeColor(orgType);
    const orgLocation = membership.organization?.city || membership.organization?.province
      ? `${membership.organization.city || ""}${membership.organization.city && membership.organization.province ? ", " : ""}${membership.organization.province || ""}`
      : null;

    return {
      id: membership.id,
      org: showUserInfo ? (
        <div className="space-y-1">
          <div className="text-sm font-medium text-text">{userName}</div>
          {userEmail && <div className="text-xs text-muted">{userEmail}</div>}
        </div>
      ) : (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="text-sm font-medium text-text">{membership.organization?.name || "—"}</div>
            <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium", orgTypeColor.bg, orgTypeColor.text)}>
              {getOrgTypeLabel(orgType)}
            </span>
          </div>
          {orgLocation && <div className="text-xs text-muted">{orgLocation}</div>}
        </div>
      ),
      role: (
        <span className="text-sm text-text">
          {getRoleLabel(membership.org_role)}
        </span>
      ),
      permissions: (
        <div className="text-xs text-text max-w-xs">
          {getPermissionsSummary(membership)}
        </div>
      ),
      status: (
        <span
          className={cn(
            "inline-flex items-center rounded-full px-2 py-1 text-xs font-medium",
            membership.status === "active"
              ? "bg-success/10 text-success"
              : "bg-warning/10 text-warning"
          )}
        >
          {membership.status === "active" ? "Actif" : "Suspendu"}
        </span>
      ),
    };
  });

  const showUserInfo = memberships.length > 0 && memberships[0].user && !memberships[0].organization;

  return (
    <DataTablePattern
      title=""
      columns={[
        { key: "org", label: showUserInfo ? "Membre" : "Organisation", type: "custom" },
        { key: "role", label: "Rôle", type: "custom" },
        { key: "permissions", label: "Permissions", type: "custom" },
        { key: "status", label: "Statut", type: "custom" },
      ]}
      rows={rows}
      rowEditButton={(row) => (
        <IconButton
          icon={<Pencil size={14} strokeWidth={1.5} />}
          ariaLabel="Modifier"
          intent="ghost"
          size="sm"
          onClick={() => {
            const membership = memberships.find((m) => m.id === row.id);
            if (membership) onEdit(membership);
          }}
        />
      )}
      rowActions={[
        ...(onSuspend
          ? [
              {
                label: (row) => {
                  const membership = memberships.find((m) => m.id === row.id);
                  return membership?.status === "active" ? "Suspendre" : "Réactiver";
                },
                onClick: (row) => {
                  onSuspend?.(row.id);
                },
              },
            ]
          : []),
        ...(onRemove
          ? [
              {
                label: "Retirer",
                onClick: (row) => {
                  if (confirm("Êtes-vous sûr de vouloir retirer cet accès ?")) {
                    onRemove(row.id);
                  }
                },
              },
            ]
          : []),
      ]}
    />
  );
}
